import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Input, Link, SectionForm, Select, Text, useTheme } from '@Sage/hoi-poi-ui';

import { PATH_IMAGE } from 'constants/Environment';
import { getLiteral } from 'utils/getLiteral';

const SenderFormFields = ({
    formData,
    errors,
    countryOptions,
    countryValue = null,
    isFreeEmailDomain,
    onFieldChange,
    onBlur,
    onCountryChange,
    onOpenMapModal,
}) => {
    const theme = useTheme();

    const handleFieldChange = useMemo(
        () => (field) => (value) => onFieldChange?.(field, value),
        [onFieldChange],
    );

    const handleBlur = useMemo(() => (field) => () => onBlur?.(field), [onBlur]);

    return (
        <div className="marketing-sender-form-modal__content">
            <Text type="body1">
                {getLiteral('label_marketing_campaigns_create_sender_text_desc')}
            </Text>
            <SectionForm
                title={getLiteral('label_information')}
                className="marketing-sender-form-modal__section"
            >
                <div className="marketing-sender-form-modal__grid marketing-sender-form-modal__grid--two">
                    <Input
                        label={getLiteral('label_from_name')}
                        isRequired
                        isFullWidth
                        value={formData.name}
                        onChange={handleFieldChange('name')}
                        onBlur={handleBlur('name')}
                        hint={getLiteral('label_marketing_campaigns_from_name_info')}
                        placeholder={getLiteral('placeholder_enter_sender_name')}
                        error={errors.name}
                    />
                    <div className="marketing-sender-form-modal__email-field">
                        <Input
                            label={getLiteral('label_from_email_address')}
                            isRequired
                            isFullWidth
                            value={formData.senderEmail}
                            onChange={handleFieldChange('senderEmail')}
                            onBlur={handleBlur('senderEmail')}
                            hint={getLiteral('label_marketing_campaigns_sender_verification_info')}
                            postComponent={
                                isFreeEmailDomain ? (
                                    <Icon
                                        name="warningOutline"
                                        size="large"
                                        color={theme.colors.semantic.caution500}
                                    />
                                ) : null
                            }
                            classes={{
                                inputComponents: isFreeEmailDomain
                                    ? 'marketing-sender-form-modal__free-email-warning'
                                    : undefined,
                            }}
                            placeholder={getLiteral('placeholder_enter_email_address')}
                            error={errors.senderEmail}
                        />
                        {isFreeEmailDomain && (
                            <div className="marketing-sender-form-modal__email-warning">
                                <div className="marketing-sender-form-modal__email-warning-text">
                                    <Text type="caption" color={theme.colors.semantic.caution500}>
                                        {getLiteral(
                                            'label_marketing_campaigns_sending_from_free_email_address_not_recommended',
                                        )}
                                    </Text>
                                </div>
                                <Link
                                    className="marketing-sender-form-modal__email-warning-link"
                                    onClick={(event) => event.preventDefault()}
                                    underline
                                >
                                    {getLiteral('label_learn_more')}
                                </Link>
                            </div>
                        )}
                    </div>
                    <Input
                        label={getLiteral('label_reply_to')}
                        isRequired
                        isFullWidth
                        value={formData.responseEmail}
                        onChange={handleFieldChange('responseEmail')}
                        onBlur={handleBlur('responseEmail')}
                        hint={getLiteral('label_marketing_campaigns_reply_to_info')}
                        placeholder={getLiteral('placeholder_enter_reply_email')}
                        error={errors.responseEmail}
                    />
                    <Input
                        label={getLiteral('label_nickname')}
                        isRequired
                        isFullWidth
                        value={formData.nickName}
                        onChange={handleFieldChange('nickName')}
                        onBlur={handleBlur('nickName')}
                        hint={getLiteral('label_marketing_campaigns_nickname_info')}
                        placeholder={getLiteral('placeholder_enter_an_identifier')}
                        error={errors.nickName}
                    />
                </div>
            </SectionForm>
            <SectionForm
                title={getLiteral('label_address')}
                className="marketing-sender-form-modal__section"
            >
                <div className="marketing-sender-form-modal__grid marketing-sender-form-modal__grid--two">
                    <Input
                        label={getLiteral('label_address')}
                        isRequired
                        isFullWidth
                        value={formData.address}
                        onChange={handleFieldChange('address')}
                        onBlur={handleBlur('address')}
                        placeholder={getLiteral('label_address')}
                        error={errors.address}
                    />
                    <Input
                        label={getLiteral('label_address_detail')}
                        isFullWidth
                        value={formData.addressDetails}
                        onChange={handleFieldChange('addressDetails')}
                        placeholder={getLiteral('placeholder_enter_address_detail')}
                    />
                </div>

                <div className="marketing-sender-form-modal__grid marketing-sender-form-modal__grid--four">
                    <Input
                        label={getLiteral('label_city')}
                        isRequired
                        isFullWidth
                        value={formData.city}
                        onChange={handleFieldChange('city')}
                        onBlur={handleBlur('city')}
                        placeholder={getLiteral('label_city')}
                        error={errors.city}
                    />
                    <Input
                        label={getLiteral('label_provinceregion')}
                        isFullWidth
                        value={formData.region}
                        onChange={handleFieldChange('region')}
                        placeholder={getLiteral('label_provinceregion')}
                    />
                    <Input
                        label={getLiteral('label_postal_code')}
                        isRequired
                        isFullWidth
                        value={formData.postalCode}
                        onChange={handleFieldChange('postalCode')}
                        onBlur={handleBlur('postalCode')}
                        placeholder={getLiteral('label_postal_code')}
                        error={errors.postalCode}
                    />
                    <Select
                        label={getLiteral('label_country')}
                        isRequired
                        isFullWidth
                        options={countryOptions}
                        value={countryValue}
                        onChange={onCountryChange}
                        placeholder={getLiteral('label_selectone')}
                        error={errors.idCountry}
                        useMenuPortal={false}
                        menuPosition="absolute"
                    />
                </div>

                <div
                    className="marketing-sender-form-modal__map-placeholder"
                    onClick={onOpenMapModal}
                >
                    <img
                        className="marketing-sender-form-modal__map-placeholder-pin"
                        src={`${PATH_IMAGE}img-pin-off.svg`}
                        alt=""
                    />
                    <Button
                        type="primary"
                        className="marketing-sender-form-modal__map-placeholder-button"
                    >
                        {getLiteral('action_mapsearch')}
                    </Button>
                </div>
            </SectionForm>
        </div>
    );
};

SenderFormFields.propTypes = {
    formData: PropTypes.shape({
        name: PropTypes.string,
        senderEmail: PropTypes.string,
        responseEmail: PropTypes.string,
        nickName: PropTypes.string,
        address: PropTypes.string,
        addressDetails: PropTypes.string,
        city: PropTypes.string,
        region: PropTypes.string,
        postalCode: PropTypes.string,
        idCountry: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        country: PropTypes.string,
    }).isRequired,
    errors: PropTypes.shape({
        name: PropTypes.string,
        senderEmail: PropTypes.string,
        responseEmail: PropTypes.string,
        nickName: PropTypes.string,
        address: PropTypes.string,
        city: PropTypes.string,
        postalCode: PropTypes.string,
        idCountry: PropTypes.string,
    }).isRequired,
    countryOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
        }),
    ).isRequired,
    countryValue: PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
    }),
    isFreeEmailDomain: PropTypes.bool.isRequired,
    onFieldChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onCountryChange: PropTypes.func.isRequired,
    onOpenMapModal: PropTypes.func.isRequired,
};

export default memo(SenderFormFields);
